// Pure selection engine for modifier groups. No React, no DOM, no network —
// so the rules that decide what a cashier may tap are covered by fast unit
// tests. The backend mirrors these rules in
// app/services/modifier_validation.py and re-checks everything on save.
//
// tree    : { [groupId]: { [optionCode]: <nested tree> } }
// path    : [[groupId, optionCode], ...] — the parent chain; [] is the root
// catalog : { groups: {id: {...}}, options: {code: {...}} }

const clone = (tree) => JSON.parse(JSON.stringify(tree));

export const nodeAt = (tree, path) => {
    let node = tree;
    for (const [groupId, optionCode] of path) {
        node = (node?.[groupId] || {})[optionCode];
        if (!node) return {};
    }
    return node;
};

export const groupNests = (group, catalog) =>
    (group?.options || []).some(code => (catalog.options[code]?.groups || []).length > 0);

export const ruleLabel = (group) => {
    const min = group.min_select || 0;
    const max = group.max_select;
    if (min === 1 && max === 1) return 'required_pick_1';
    if (min === 0 && max === 1) return 'optional_pick_1';
    if (min > 0 && max) return 'required_pick_range';
    if (min > 0) return 'required_pick_min';
    if (max) return 'optional_up_to_n';
    return 'optional_pick_any';
};

export const toggleOption = (tree, path, groupId, optionCode, catalog) => {
    const next = clone(tree);

    // Walk to the parent node, creating containers as needed.
    let node = next;
    for (const [gid, code] of path) {
        node[gid] = node[gid] || {};
        node[gid][code] = node[gid][code] || {};
        node = node[gid][code];
    }

    const group = catalog.groups[groupId];
    if (!group) return next;

    const bucket = node[groupId] || (node[groupId] = {});

    if (optionCode in bucket) {
        // Deselecting discards the option's entire subtree.
        delete bucket[optionCode];
        return next;
    }

    const max = group.max_select;
    if (max === 1) {
        Object.keys(bucket).forEach(key => delete bucket[key]);
    } else if (max != null && Object.keys(bucket).length >= max) {
        return next;  // at the ceiling — the tap does nothing
    }

    bucket[optionCode] = {};
    return next;
};

const orderedGroups = (groupIds, catalog) =>
    (groupIds || [])
        .map(id => catalog.groups[id])
        .filter(Boolean)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

const orderedChosen = (node, group, catalog) =>
    (group.options || [])
        .filter(code => code in (node[group.group_id] || {}))
        .map(code => catalog.options[code])
        .filter(Boolean);

export const selectionTotal = (tree, groupIds, catalog) => {
    let sum = 0;
    for (const group of orderedGroups(groupIds, catalog)) {
        for (const option of orderedChosen(tree, group, catalog)) {
            sum += Number(option.price) || 0;
            sum += selectionTotal(
                tree[group.group_id][option.product_code], option.groups, catalog);
        }
    }
    return sum;
};

export const unmetGroups = (tree, groupIds, catalog, trail = []) => {
    const problems = [];
    for (const group of orderedGroups(groupIds, catalog)) {
        const bucket = tree[group.group_id] || {};
        const count = Object.keys(bucket).length;
        const min = group.min_select || 0;
        const max = group.max_select;
        if (count < min || (max != null && count > max)) {
            problems.push([...trail, group.name].join(' → '));
        }
        for (const option of orderedChosen(tree, group, catalog)) {
            problems.push(...unmetGroups(
                bucket[option.product_code], option.groups, catalog,
                [...trail, option.name]));
        }
    }
    return problems;
};

export const flattenSelection = (tree, groupIds, catalog,
                                parentCode = null, depth = 0, out = []) => {
    for (const group of orderedGroups(groupIds, catalog)) {
        for (const option of orderedChosen(tree, group, catalog)) {
            out.push({
                product_code: option.product_code,
                name: option.name,
                price: Number(option.price) || 0,
                group_id: group.group_id,
                parent_code: parentCode,
                depth,
            });
            flattenSelection(
                tree[group.group_id][option.product_code], option.groups, catalog,
                option.product_code, depth + 1, out);
        }
    }
    return out;
};
